import { Text } from '@react-pdf/renderer'
import { Style } from '@react-pdf/types'
import parse, { domToReact } from 'html-react-parser'
import React, { ReactNode } from 'react'
import rehypeStringify from 'rehype-stringify'
import remarkParse from 'remark-parse'
import remarkRehype from 'remark-rehype'
import { unified } from 'unified'
import { ImprovedHtml } from '../common-components/ImprovedHtml'

/**
 * Parses a given label string and returns a string, a parsed HTML React node or
 * a PDF-compatible component.
 *
 * @function
 * @category Utils - Parsing
 * @param {string | ReactNode} label - The input label, which may contain plain text, or HTML.
 * @param {boolean} [returnPdf=false] - Whether to return a component for PDF rendering
 * (ImprovedHtml).
 * @param {boolean} [collapse=false] - If true, collapses spacing in the ImprovedHtml
 * component.
 * @param {Style} [style] - Optional inline styles passed to the `Text` or
 * `ImprovedHtml` component.
 * @param {Object<string, Style>} [stylesheet] - Optional stylesheet used for styling HTML
 * tags when rendering in PDF via the `ImprovedHtml` component.
 * @returns {React.ReactNode} Returns either a plain string, a parsed React node (with line breaks or HTML),
 * or a PDF-compatible component (`Text` or `ImprovedHtml`).
 */
export const parseLabel = (
  label: string | ReactNode,
  returnPdf: boolean = false,
  collapse: boolean = false,
  style?: Style,
  stylesheet?: Record<string, Style>
): ReactNode => {
  if (!label) return ''

  if (typeof label !== 'string') {
    return label as ReactNode
  }

  const containsHTML = /<[^>]+>/i.test(label)

  if (!containsHTML) {
    if (returnPdf) {
      return <Text style={style}>{label}</Text>
    }

    if (label.includes('\n')) {
      const labelWithBreaks = label.replace(/\n/g, '<br />')
      return parse(labelWithBreaks)
    }

    return label
  }

  const processedLabel = unified()
    .use(remarkParse)
    .use(remarkRehype, { allowDangerousHtml: true })
    .use(rehypeStringify, { allowDangerousHtml: true })
    .processSync(label)
    .toString()

  if (returnPdf) {
    return (
      <ImprovedHtml style={style} stylesheet={stylesheet} collapse={collapse}>
        {processedLabel}
      </ImprovedHtml>
    )
  }

  return (
    <span className='form-parsed-text'>
      {parse(processedLabel, {
        replace: (domNode) => {
          if (domNode?.name === 'p') {
            return <>{domToReact(domNode.children)}</>
          }
        }
      })}
    </span>
  )
}
